function stableTimetableStringify(value) {
  const ordered = input => Array.isArray(input) ? input.map(ordered) : input && typeof input === 'object'
    ? Object.fromEntries(Object.keys(input).sort().map(key => [key, ordered(input[key])])) : input
  return JSON.stringify(ordered(value))
}

/**
 * src/services/userDataService.js
 * 유저 데이터 서비스 레이어 — Firestore primary, localStorage offline cache
 *
 * 원칙:
 *   - Firestore를 primary 소스로, localStorage를 오프라인 캐시로 사용
 *   - 읽기: Firestore 우선 → 실패 시 localStorage fallback
 *   - 쓰기: localStorage 즉시(동기) + Firestore 비동기(실패해도 앱 동작)
 *   - db가 null(Firebase 미설정)이면 localStorage만 사용
 *
 * Firestore 스키마:
 *   users/{uid}                          프로필 + 메타데이터
 *   users/{uid}/timetables/{semesterKey} 학기별 시간표 (과목 + 설정) — semesterKey 예: '2026-2'
 *   users/{uid}/timetables/current       학기 개념 도입 이전 legacy 시간표. 새로 쓰지 않음.
 *                                        LEGACY_TIMETABLE_SEMESTER_KEY 조회 시에만 read-only 폴백으로 노출.
 *   users/{uid}/savedTimetables/{id}     저장된 시간표 후보 (semesterKey 필드로 학기 구분)
 *   users/{uid}/grades/data              학기별 학점
 */

import { db } from '../lib/firebase'
import { timetableReadEpoch, invalidateTimetableReadEpoch } from './timetableReadEpoch'
import {
  doc, getDoc, setDoc, deleteDoc,
  collection, getDocs, query, serverTimestamp, where, runTransaction,
} from 'firebase/firestore'
import { normalizeAcademicProfile } from '../utils/profileManagement'
import { reconcileCourseClassroom, reconcileSavedTimetableClassrooms, reconcileTimetableClassrooms } from '../utils/timetableClassroom'
import { normalizeCourseIsOnline } from '../utils/courseScheduleMode'
import { syncFriendTimetable } from './friendService'
import { MAX_SAVES } from '../utils/savedTimetableUtils'
import { timetableContentKey } from '../utils/timetableIdentity'

// 시간표 데이터가 아직 학기 구분 없이 저장되던 시절의 데이터를 귀속시키는 경계.
// 일반적인 "이전 학기 계산" 함수가 아니다 — 이 한 번의 마이그레이션 지점에서만 쓰인다.
export const LEGACY_TIMETABLE_SEMESTER_KEY = '2026-1'

// ── 로컬 캐시 키 ──────────────────────────────────────────────────────────────
// 기존 키(su-guide-timetable-v3, suguide_timetable_{uid}, suguide_saved_timetables 등)는
// migration.js에서 새 키로 이전 후 읽기를 교체. 새 코드는 아래 키만 사용.
// timetable/savedTimetables/activeId는 semesterKey로 스코프된다 — 학기가 다르면 완전히 다른 슬롯.
function requireSemesterKey(semesterKey) {
  if (!semesterKey) throw new Error('[UserData] semesterKey is required')
  return semesterKey
}
export const CACHE = {
  timetable:       (uid, semesterKey) => `suguide:tt:${uid}:${requireSemesterKey(semesterKey)}`,
  savedTimetables: (uid, semesterKey) => `suguide:saves:${uid}:${requireSemesterKey(semesterKey)}`,
  grades:          (uid) => `suguide:grades:${uid}`,
  activeId:        (uid, semesterKey) => `suguide:activeId:${uid}:${requireSemesterKey(semesterKey)}`,
}

export const ANON_GRADE_KEY = 'su-guide-grades'

export function normalizeGradeData(data) {
  const gradeSemesters = Array.isArray(data?.gradeSemesters)
    ? data.gradeSemesters
    : Array.isArray(data?.semesters) ? data.semesters : []
  return { ...data, gradeSemesters }
}

// ── localStorage 헬퍼 ─────────────────────────────────────────────────────────
function lsGet(key) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : null } catch { return null }
}
function lsSet(key, value) {
  try { if (value != null) localStorage.setItem(key, JSON.stringify(value)) } catch {}
}
function lsRemove(key) {
  try { localStorage.removeItem(key) } catch {}
}

export function findUndefinedPaths(value, path = '') {
  const paths = []
  if (value === undefined) return [path || '<root>']
  if (Array.isArray(value)) {
    value.forEach((item, index) => paths.push(...findUndefinedPaths(item, `${path}[${index}]`)))
  } else if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    Object.entries(value).forEach(([key, item]) => {
      const next = path ? `${path}.${key}` : key
      paths.push(...findUndefinedPaths(item, next))
    })
  }
  return paths
}

export function removeUndefinedDeep(value) {
  if (Array.isArray(value)) return value.filter(item => item !== undefined).map(removeUndefinedDeep)
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .map(([key, item]) => [key, removeUndefinedDeep(item)]))
  }
  return value
}

function finiteNumberOrNull(value) {
  if (value == null || value === '') return null
  const number = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(number) ? number : null
}

export function normalizeTimetableCourse(course = {}) {
  const clean = removeUndefinedDeep(course)
  const isOnline = normalizeCourseIsOnline(course)
  const startPeriod = finiteNumberOrNull(course.startPeriod)
  const endPeriod = finiteNumberOrNull(course.endPeriod)
  const firstScheduledMeeting = Array.isArray(course.meetings)
    ? course.meetings.find(meeting => {
      const days = Array.isArray(meeting?.days) ? meeting.days : (meeting?.day ? [meeting.day] : [])
      return days.length > 0 && (Array.isArray(meeting?.periods) ? meeting.periods.length > 0 : meeting?.startPeriod != null)
    })
    : null
  const meetingPeriods = Array.isArray(firstScheduledMeeting?.periods) ? firstScheduledMeeting.periods : []
  const meetingStart = meetingPeriods.length ? Math.min(...meetingPeriods) : finiteNumberOrNull(firstScheduledMeeting?.startPeriod)
  const meetingEnd = meetingPeriods.length ? Math.max(...meetingPeriods) : finiteNumberOrNull(firstScheduledMeeting?.endPeriod ?? meetingStart)
  // Online classification never removes stored schedule metadata. The grid
  // decides placement from canonical isOnline after hydration.
  const hasTime = (
    (startPeriod != null && endPeriod != null) || (meetingStart != null && meetingEnd != null)
  )
  const courseDays = Array.isArray(course.days)
    ? course.days
    : course.day != null && course.day !== '' ? [course.day] : []
  const name = String(course.name ?? course.courseName ?? '')
  const professor = String(course.professor ?? '')
  const classroom = String(course.classroom ?? course.room ?? '')
  const credits = finiteNumberOrNull(course.credits ?? course.credit)
  return reconcileCourseClassroom({
    ...clean,
    name,
    courseName: String(course.courseName ?? name),
    professor,
    classroom,
    room: String(course.room ?? classroom),
    credits,
    credit: finiteNumberOrNull(course.credit ?? credits),
    days: hasTime ? (courseDays.length ? courseDays.filter(day => day !== undefined) : (Array.isArray(firstScheduledMeeting?.days) ? firstScheduledMeeting.days : [firstScheduledMeeting?.day]).filter(Boolean)) : [],
    startPeriod: hasTime ? (startPeriod ?? meetingStart) : null,
    endPeriod: hasTime ? (endPeriod ?? meetingEnd) : null,
    // Keep old ordinary records byte-compatible; legacy online metadata is
    // promoted explicitly to true at this hydration boundary.
    ...(isOnline || course.isOnline === false ? { isOnline } : {}),
  })
}

export function normalizeTimetablePayload(data = {}) {
  const undefinedPaths = findUndefinedPaths(data)
  if (import.meta.env.DEV && undefinedPaths.length > 0) {
    console.warn('[UserData] timetable undefined fields:', undefinedPaths)
  }
  const clean = removeUndefinedDeep(data)
  return reconcileTimetableClassrooms({
    ...clean,
    courses: Array.isArray(data.courses) ? data.courses.filter(Boolean).map(normalizeTimetableCourse) : [],
    selectedCourses: Array.isArray(data.selectedCourses)
      ? data.selectedCourses.filter(Boolean).map(normalizeTimetableCourse)
      : [],
  })
}

function assertStableCourseIds(courses) {
  const invalidIndex = courses.findIndex(course => String(course?.id ?? '').trim() === '')
  if (invalidIndex < 0) return
  const error = new Error(`Timetable course at index ${invalidIndex} has no stable id`)
  error.code = 'invalid-timetable-course-id'
  throw error
}

// ── Firestore 가용성 ───────────────────────────────────────────────────────────
function fsOk(uid) { return Boolean(db && uid) }

// ── Firestore 경로 ────────────────────────────────────────────────────────────
const ref = {
  user:            (uid)             => doc(db, 'users', uid),
  timetable:       (uid, semesterKey) => doc(db, 'users', uid, 'timetables', requireSemesterKey(semesterKey)),
  legacyTimetable: (uid)             => doc(db, 'users', uid, 'timetables', 'current'),
  saves:           (uid)             => collection(db, 'users', uid, 'savedTimetables'),
  save:            (uid, id)         => doc(db, 'users', uid, 'savedTimetables', id),
  grades:          (uid)             => doc(db, 'users', uid, 'grades', 'data'),
}

// 동일 uid/학기 읽기는 앱 진입 동기화와 허브 revalidation이 겹쳐도 하나의
// Firestore 요청만 사용한다. 완료/실패 후에는 제거해 다음 명시적 갱신을 허용한다.
const timetableReadInFlight = new Map()
const savedTimetablesReadInFlight = new Map()
// An explicit mutation that begins while a read is in flight must not be
// mistaken for an old display cache when that read resolves. This is process
// local on purpose: it guards only the current lifecycle race; Firestore
// remains the cross-device source of truth on the next successful hydration.
const timetableMutationGeneration = new Map()
const savedTimetablesMutationGeneration = new Map()
const pendingSavedCreates = new Map()

function readKey(uid, semesterKey) {
  return `${uid}:${semesterKey}`
}

// ── Firestore 쓰기 (fire-and-forget — 실패해도 앱 동작) ───────────────────────
function fsSet(docRef, data, merge = true) {
  setDoc(docRef, data, { merge })
    .catch(err => console.warn('[UserData] Firestore write failed:', err.code ?? err.message))
}
function fsDel(docRef) {
  deleteDoc(docRef)
    .catch(err => console.warn('[UserData] Firestore delete failed:', err.code ?? err.message))
}

// ──────────────────────────────────────────────────────────────────────────────
// 1. 유저 문서 upsert (로그인 직후 호출)
//    없으면 생성, 있으면 lastLoginAt 갱신만
// ──────────────────────────────────────────────────────────────────────────────
export async function upsertUserDoc(uid, userInfo = {}) {
  if (!fsOk(uid)) return
  try {
    const snap = await getDoc(ref.user(uid))
    if (!snap.exists()) {
      fsSet(ref.user(uid), {
        uid,
        provider:    userInfo.provider    ?? 'kakao',
        displayName: userInfo.displayName ?? null,
        email:       userInfo.email       ?? null,
        photoURL:    userInfo.photoURL    ?? null,
        // 신규 문서는 온보딩 미완료로 명시한다 — 소셜 로그인 displayName이 어떤
        // 경로로든 nickname에 흘러들어가도(또는 향후 그런 코드가 추가되어도)
        // isValidProfile만으로 온보딩을 건너뛰지 못하도록 하는 안전장치.
        onboardingCompleted: false,
        createdAt:   serverTimestamp(),
        updatedAt:   serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      }, false)  // merge: false → 신규 문서이므로 덮어쓰기
    } else {
      fsSet(ref.user(uid), { lastLoginAt: serverTimestamp(), updatedAt: serverTimestamp() })
    }
  } catch (err) {
    console.warn('[UserData] upsertUserDoc failed:', err.code ?? err.message)
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// 2. 프로필 (닉네임, 학과, 학년 등)
//    프로필 캐시는 userProfile.js의 기존 키('suguide_user_profile')와 공유
// ──────────────────────────────────────────────────────────────────────────────
const PROFILE_CACHE_KEY = 'suguide_user_profile'

// getDoc 실패를 여기서 삼키지 않고 호출부(AuthContext)로 전파한다 — 호출부가
// "Firestore 조회 실패"와 "문서는 있지만 필드 없음"을 구분해 온보딩 게이트를 판단한다.
export async function getProfile(uid) {
  if (!fsOk(uid)) return lsGet(PROFILE_CACHE_KEY)
  const snap = await getDoc(ref.user(uid))
  if (snap.exists()) {
    const d = snap.data()
    const profile = normalizeAcademicProfile({
      nickname:            d.nickname     ?? null,
      college:             d.college      ?? null,
      department:          d.department   ?? null,
      grade:               d.grade        ?? null,
      entranceYear:        d.entranceYear ?? null,
      // true / false / undefined 그대로 전달 — isOnboardingComplete가 3상태로 판단한다.
      // Boolean(...)으로 강제하면 "필드 없음(레거시)"과 "false(신규 미완료)"가
      // 구분되지 않아 온보딩 판정이 틀어진다.
      onboardingCompleted: d.onboardingCompleted,
    })
    // nickname 있을 때만 로컬 캐시를 갱신한다 (빈 문서로 캐시를 덮어쓰지 않도록).
    // 단, 반환값은 항상 Firestore 기준 profile — 캐시가 다른 계정의 값을 들고
    // 있을 수 있으므로(공유 기기 재로그인 등) nickname이 비어 있다고 캐시로
    // 대체해 버리면 방금 생성된 신규 문서의 onboardingCompleted:false가 가려진다.
    if (profile.nickname) {
      lsSet(PROFILE_CACHE_KEY, profile)
    }
    if (import.meta.env.DEV) console.log('[Onboarding] 앱 시작 프로필 로드 — onboardingCompleted =', profile.onboardingCompleted,
      '| nickname =', profile.nickname ?? '(없음)', '| department =', profile.department ?? '(없음)')
    return profile
  }
  return lsGet(PROFILE_CACHE_KEY)
}

// patch만 받는 순수 Firestore 쓰기 함수 — 로컬 캐시는 건드리지 않는다(AuthContext가 전담).
// completeOnboarding/skipOnboarding/saveGrade와 동일하게 fire-and-forget이 아니라 실패를
// 그대로 throw해 호출부가 "저장 성공"을 가장하지 않게 한다.
export async function saveProfile(uid, patch) {
  if (!fsOk(uid)) {
    throw new Error('오프라인 상태에서는 저장할 수 없어요. 네트워크 연결을 확인해 주세요.')
  }
  await setDoc(ref.user(uid), { ...patch, updatedAt: serverTimestamp() }, { merge: true })
}

// 학년만 갱신 — saveProfile과 달리 fire-and-forget이 아니라 실패를 호출부로
// 그대로 전파한다(completeOnboarding과 동일 패턴). 성공 여부를 UI가 알아야
// 학년 저장 실패를 성공으로 가장하지 않을 수 있다.
export async function saveGrade(uid, grade) {
  if (!fsOk(uid)) {
    throw new Error('오프라인 상태에서는 저장할 수 없어요. 네트워크 연결을 확인해 주세요.')
  }
  await setDoc(ref.user(uid), { grade, updatedAt: serverTimestamp() }, { merge: true })
}

// 시간표 시작 시 college/department/grade를 한 번에 확정 저장 — saveGrade와 동일 패턴
// (fire-and-forget이 아니라 실패를 그대로 전파). 다른 프로필 필드는 건드리지 않는 merge 쓰기.
export async function saveStudentContext(uid, { college, department, grade }) {
  if (!fsOk(uid)) {
    throw new Error('오프라인 상태에서는 저장할 수 없어요. 네트워크 연결을 확인해 주세요.')
  }
  await setDoc(ref.user(uid), { college, department, grade, updatedAt: serverTimestamp() }, { merge: true })
}

// 온보딩 완료 저장 — saveProfile과 달리 fire-and-forget이 아니라 Firestore 쓰기가
// 끝날 때까지 await한다. 저장 성공 후에만 로컬 캐시를 갱신해 실패 시 완료 상태가
// 로컬에서 임의로 true가 되지 않도록 한다. 기존 문서를 덮어쓰지 않는 merge 쓰기.
export async function completeOnboarding(uid, profileData) {
  if (!fsOk(uid)) {
    throw new Error('오프라인 상태에서는 저장할 수 없어요. 네트워크 연결을 확인해 주세요.')
  }
  const payload = {
    nickname:              profileData.nickname,
    college:               profileData.college ?? '',
    department:            profileData.department ?? '',
    grade:                 profileData.grade ?? '',
    entranceYear:          profileData.entranceYear ?? '',
    onboardingCompleted:   true,
    onboardingCompletedAt: serverTimestamp(),
    updatedAt:             serverTimestamp(),
  }
  await setDoc(ref.user(uid), payload, { merge: true })
  const cached = {
    nickname:            payload.nickname,
    college:             payload.college,
    department:          payload.department,
    grade:               payload.grade,
    entranceYear:         payload.entranceYear,
    onboardingCompleted: true,
  }
  lsSet(PROFILE_CACHE_KEY, cached)
  return cached
}

// 건너뛰기는 완료 플래그만 세운다 — 닉네임/학과 등 기존 프로필 필드는 절대
// 건드리지 않는다. (관리자 '온보딩 다시 보기' 후 건너뛰기처럼 이미 입력돼 있던
// 데이터가 있는 계정에서, 빈 문자열로 merge 저장하면 Firestore·로컬 캐시의
// 기존 닉네임·학과가 지워지므로 별도 경로로 분리했다.) 신규 사용자는 보존할
// 데이터가 없어 완료 플래그만 세우는 동일 동작으로 수렴한다.
export async function skipOnboarding(uid) {
  if (!fsOk(uid)) {
    throw new Error('오프라인 상태에서는 저장할 수 없어요. 네트워크 연결을 확인해 주세요.')
  }
  if (import.meta.env.DEV) {
    const before = lsGet(PROFILE_CACHE_KEY)
    console.log('[Onboarding] skip 시작 — 저장 전 onboardingCompleted =', before?.onboardingCompleted,
      '| nickname =', before?.nickname ?? '(없음)', '| department =', before?.department ?? '(없음)')
  }
  try {
    await setDoc(ref.user(uid), {
      onboardingCompleted:   true,
      onboardingCompletedAt: serverTimestamp(),
      updatedAt:             serverTimestamp(),
    }, { merge: true })
    if (import.meta.env.DEV) console.log('[Onboarding] Firestore 저장 성공 — onboardingCompleted:true (닉네임/학과 필드 미변경)')
  } catch (err) {
    if (import.meta.env.DEV) console.error('[Onboarding] Firestore 저장 실패:', err?.code ?? err?.message)
    throw err
  }
  // 저장 성공 후에만 로컬 캐시를 갱신한다. 기존 캐시를 읽어 완료 플래그만 덮어써
  // 닉네임·학과 등 이미 있던 필드를 그대로 보존한다.
  const existing = lsGet(PROFILE_CACHE_KEY) ?? {}
  const cached = { ...existing, onboardingCompleted: true }
  lsSet(PROFILE_CACHE_KEY, cached)
  if (import.meta.env.DEV) console.log('[Onboarding] 로컬 캐시 갱신 완료 — onboardingCompleted =', cached.onboardingCompleted,
    '| nickname 보존 =', cached.nickname ?? '(없음)', '| department 보존 =', cached.department ?? '(없음)')
  return cached
}

// ──────────────────────────────────────────────────────────────────────────────
// 3. 현재 시간표 (과목 목록 + preferences + 메타데이터)
// ──────────────────────────────────────────────────────────────────────────────

export function getLocalTimetable(uid, semesterKey, storage = globalThis.localStorage) {
  requireSemesterKey(semesterKey)
  try {
    const raw = storage?.getItem(CACHE.timetable(uid, semesterKey))
    return raw ? reconcileTimetableClassrooms(JSON.parse(raw)) : null
  } catch { return null }
}

export function getLocalSavedTimetables(uid, semesterKey, storage = globalThis.localStorage) {
  requireSemesterKey(semesterKey)
  try {
    const raw = storage?.getItem(CACHE.savedTimetables(uid, semesterKey))
    return raw ? reconcileSavedTimetableClassrooms(JSON.parse(raw)) : []
  } catch { return [] }
}

async function fetchTimetableHydration(uid, semesterKey, local, mutationGeneration) {
  const ownerEpoch=timetableReadEpoch(uid)
  try {
    const snap = await getDoc(ref.timetable(uid, semesterKey))
    if (snap.metadata?.fromCache) return { timetable: getLocalTimetable(uid, semesterKey), authoritative: false }
    if (ownerEpoch!==timetableReadEpoch(uid)||(timetableMutationGeneration.get(readKey(uid, semesterKey)) ?? 0) !== mutationGeneration) {
      return { timetable: getLocalTimetable(uid, semesterKey), authoritative: false, superseded: true }
    }
    if (snap.exists()) {
      const remote = reconcileTimetableClassrooms(snap.data())
      // A successful Firestore read is the canonical answer. A device clock is
      // neither a revision protocol nor proof that a cache represents an
      // unsynced mutation, so it must never reverse this direction or cause a
      // hydration write-back.
      lsSet(CACHE.timetable(uid, semesterKey), remote)
      return { timetable: remote, authoritative: true }
    }
    // Missing is also an authoritative cloud answer. Cache data can be shown
    // only when the read failed/offline; it must not resurrect or create a
    // canonical document after this successful read.
    const legacy = await legacyTimetableFallback(uid, semesterKey)
    if(ownerEpoch!==timetableReadEpoch(uid))return {timetable:getLocalTimetable(uid,semesterKey),authoritative:false,superseded:true}
    if (legacy) return { timetable: legacy, authoritative: true }
    const empty = { courses: [] }
    lsSet(CACHE.timetable(uid, semesterKey), empty)
    // 이 semesterKey에 저장된 데이터가 전혀 없음. 오직 legacy 경계(2026-1)에 대해서만
    // 학기 개념 도입 이전의 단일 'current' 문서를 read-only로 조회해 귀속시킨다 —
    // 다른 어떤 semesterKey에도 이 폴백은 절대 적용되지 않는다.
    return { timetable: empty, authoritative: true }
  } catch {
    // A cache is useful for rendering, but a failed cloud read cannot prove
    // which planning identities are stale.
    return { timetable: getLocalTimetable(uid, semesterKey), authoritative: false }
  }
}

export async function getTimetableHydration(uid, semesterKey) {
  requireSemesterKey(semesterKey)
  const local = getLocalTimetable(uid, semesterKey)
  if (!fsOk(uid)) return { timetable: local, authoritative: false }
  const key = readKey(uid, semesterKey)
  const epoch=timetableReadEpoch(uid)
  if (timetableReadInFlight.get(key)?.epoch===epoch) return timetableReadInFlight.get(key).promise
  const request = fetchTimetableHydration(uid, semesterKey, local, timetableMutationGeneration.get(key) ?? 0)
    .finally(() => {if(timetableReadInFlight.get(key)?.promise===request)timetableReadInFlight.delete(key)})
  timetableReadInFlight.set(key, {epoch,promise:request})
  return request
}

export async function getTimetable(uid, semesterKey) {
  const hydration = await getTimetableHydration(uid, semesterKey)
  return hydration.timetable
}

async function legacyTimetableFallback(uid, semesterKey) {
  if (semesterKey !== LEGACY_TIMETABLE_SEMESTER_KEY) return null
  const snap = await getDoc(ref.legacyTimetable(uid))
  if (snap.metadata?.fromCache) throw new Error('Legacy cache is not authoritative')
  return snap.exists() ? snap.data() : null
}

export async function saveTimetable(uid, semesterKey, data, {
  throwOnError = false,
  baseRevision,
} = {}) {
  requireSemesterKey(semesterKey)
  if (!uid) return null
  const payload = normalizeTimetablePayload(data)
  assertStableCourseIds(payload.courses)
  const key = readKey(uid, semesterKey)
  timetableMutationGeneration.set(key, (timetableMutationGeneration.get(key) ?? 0) + 1)
  invalidateTimetableReadEpoch(uid)
  const expectedRevision = Number.isFinite(Number(baseRevision))
    ? Number(baseRevision)
    : Number(payload.revision) || 0

  if (!fsOk(uid)) {
    const localPayload = { ...payload, revision: expectedRevision }
    lsSet(CACHE.timetable(uid, semesterKey), localPayload)
    return localPayload
  }

  let committedPayload
  try {
    await runTransaction(db, async transaction => {
      const currentSnap = await transaction.get(ref.timetable(uid, semesterKey))
      const currentRevision = currentSnap.exists() ? Number(currentSnap.data()?.revision) || 0 : 0
      if (currentRevision > expectedRevision) {
        const error = new Error('Timetable write rejected: stale revision')
        error.code = 'stale-timetable-write'
        error.currentRevision = currentRevision
        throw error
      }

      const committedRevision = currentRevision + 1
      committedPayload = { ...payload, revision: committedRevision }
      transaction.set(ref.timetable(uid, semesterKey), {
        ...committedPayload,
        updatedAt: serverTimestamp(),
      }, { merge: true })
    })
  } catch (err) {
    console.warn('[UserData] Firestore timetable write failed:', err.code ?? err.message)
    if (throwOnError) throw err
    return null
  }

  // Signed-in cache contains acknowledged canonical data only. Failed writes
  // remain UI error state and cannot be revived by later hydration.
  lsSet(CACHE.timetable(uid, semesterKey), committedPayload)

  // Public friend data is a secondary projection. Once the canonical transaction
  // commits, projection failure must not turn that commit into a timetable error.
  syncFriendTimetable({ uid, semesterKey, courses: committedPayload.courses }).catch(err => {
    console.warn('[UserData] friend timetable projection sync failed:', err.code ?? err.message)
  })
  return committedPayload
}

// Firestore에서 강제로 최신 시간표를 읽는다 (타임스탬프 비교 없이 항상 원격 우선).
// - 존재/부재 모두 canonical remote 답이다. 부재는 빈 시간표로 적용해야 하며, caller가
//   기존 local state를 유지한 채 "동기화됨"이라고 표시해서는 안 된다.
// - 네트워크 오류는 throw (caller가 cache/offline 상태로 처리)
// - Firestore에 쓰기는 절대 하지 않는다
export async function forceFetchTimetable(uid, semesterKey) {
  requireSemesterKey(semesterKey)
  if (!fsOk(uid)) throw new Error('Firestore is unavailable')
  const epoch = timetableMutationGeneration.get(readKey(uid, semesterKey)) ?? 0, ownerEpoch=timetableReadEpoch(uid)
  const snap = await getDoc(ref.timetable(uid, semesterKey))
  if (snap.metadata?.fromCache) throw new Error('Server confirmation is unavailable')
  if (ownerEpoch!==timetableReadEpoch(uid)||(timetableMutationGeneration.get(readKey(uid, semesterKey)) ?? 0) !== epoch) return { timetable: getLocalTimetable(uid, semesterKey), authoritative: false }
  const timetable = snap.exists() ? reconcileTimetableClassrooms(snap.data()) : { courses: [] }
  lsSet(CACHE.timetable(uid, semesterKey), timetable)
  return { timetable, authoritative: true }
}

// ──────────────────────────────────────────────────────────────────────────────
// 4. 저장된 시간표 목록
// ──────────────────────────────────────────────────────────────────────────────

// entry.semesterKey 없는 legacy 엔트리는 LEGACY_TIMETABLE_SEMESTER_KEY 소속으로 취급한다.
function belongsToSemester(entry, semesterKey) {
  return entry.semesterKey === semesterKey
    || (!entry.semesterKey && semesterKey === LEGACY_TIMETABLE_SEMESTER_KEY)
}

async function fetchSavedTimetables(uid, semesterKey, mutationGeneration) {
  const ownerEpoch=timetableReadEpoch(uid)
  try {
    // semesterKey가 없던 legacy 저장본은 2026-1에서만 호환 조회한다. 현재/이후
    // 학기는 Firestore에서 먼저 좁혀 전체 컬렉션 다운로드를 피한다.
    const savesRef = semesterKey === LEGACY_TIMETABLE_SEMESTER_KEY
      ? ref.saves(uid)
      : query(ref.saves(uid), where('semesterKey', '==', semesterKey))
    const snaps = await getDocs(savesRef)
    if (snaps.metadata?.fromCache) return getLocalSavedTimetables(uid, semesterKey)
    const remote = reconcileSavedTimetableClassrooms(snaps.docs
      .map(d => ({ ...d.data(), id: d.id }))
      .filter(entry => belongsToSemester(entry, semesterKey))
      .sort((a, b) => {
        const ta = a.createdAt?.seconds ?? new Date(a.createdAt ?? 0).getTime() / 1000
        const tb = b.createdAt?.seconds ?? new Date(b.createdAt ?? 0).getTime() / 1000
        return ta - tb
      }))
    if (ownerEpoch!==timetableReadEpoch(uid)||(savedTimetablesMutationGeneration.get(readKey(uid, semesterKey)) ?? 0) !== mutationGeneration) return getLocalSavedTimetables(uid, semesterKey)
    // A successful collection query, including an empty result, is the
    // canonical saved-plan list. Hydration is read-only: it never merges or
    // uploads device-only entries based on client timestamps.
    lsSet(CACHE.savedTimetables(uid, semesterKey), remote)
    return remote
  } catch {}
  return getLocalSavedTimetables(uid, semesterKey)
}

export async function getSavedTimetables(uid, semesterKey) {
  requireSemesterKey(semesterKey)
  const local = getLocalSavedTimetables(uid, semesterKey)
  if (!fsOk(uid)) return local
  const key = readKey(uid, semesterKey)
  const epoch=timetableReadEpoch(uid)
  if (savedTimetablesReadInFlight.get(key)?.epoch===epoch) return savedTimetablesReadInFlight.get(key).promise
  const request = fetchSavedTimetables(uid, semesterKey, savedTimetablesMutationGeneration.get(key) ?? 0)
    .finally(() => {if(savedTimetablesReadInFlight.get(key)?.promise===request)savedTimetablesReadInFlight.delete(key)})
  savedTimetablesReadInFlight.set(key, {epoch,promise:request})
  return request
}

export function saveTimetableEntry(uid, entry, semesterKey, options = {}) {
  requireSemesterKey(semesterKey)
  if (!uid || !entry?.id) return
  const key = readKey(uid, semesterKey)
  const cached = lsGet(CACHE.savedTimetables(uid, semesterKey)) ?? []
  const idx    = cached.findIndex(e => e.id === entry.id)
  const pending = pendingSavedCreates.get(key) ?? new Map()
  const pendingMissing = [...pending.keys()].filter(id => !cached.some(item => item.id === id)).length
  if (idx < 0 && !pending.has(entry.id) && cached.length + pendingMissing >= MAX_SAVES) {
    // 5개 한도 도달 시 신규 문서 Firestore 생성 및 캐시 추가 차단
    if (options.waitForRemote) return Promise.reject(new Error('TIMETABLE_SAVE_LIMIT'))
    return
  }
  savedTimetablesMutationGeneration.set(key, (savedTimetablesMutationGeneration.get(key) ?? 0) + 1)
  invalidateTimetableReadEpoch(uid)
  const stamped = reconcileSavedTimetableClassrooms([{ ...entry, semesterKey: entry.semesterKey ?? semesterKey }])[0]
  if (options.waitForRemote) {
    if (!fsOk(uid)) return Promise.reject(new Error('TIMETABLE_REMOTE_UNAVAILABLE'))
    if (idx < 0) {
      pending.set(entry.id, (pending.get(entry.id) ?? 0) + 1)
      pendingSavedCreates.set(key, pending)
    }
    let committed = stamped
    const write = options.expectedEntry || options.createOnly ? runTransaction(db, async transaction => {
      const document = ref.save(uid, stamped.id)
      const remote = await transaction.get(document)
      if (options.createOnly) {
        if (remote.exists()) {
          if (timetableContentKey(remote.data()) !== timetableContentKey(stamped)) {
            const error = new Error('Snapshot identity already belongs to an edited timetable')
            error.code = 'saved-timetable-id-conflict'
            throw error
          }
          committed = { ...remote.data(), id: stamped.id }
          return
        }
        transaction.set(document, removeUndefinedDeep(stamped))
        return
      }
      const fingerprint = value => stableTimetableStringify({ name: value?.name ?? '', courses: normalizeTimetablePayload({ courses: value?.courses ?? [] }).courses })
      if (!remote.exists() || fingerprint(remote.data()) !== fingerprint(options.expectedEntry)) {
        const error = new Error('Saved timetable changed on another device')
        error.code = 'stale-saved-timetable-write'
        throw error
      }
      transaction.set(document, removeUndefinedDeep(stamped), { merge: true })
    }) : setDoc(ref.save(uid, stamped.id), removeUndefinedDeep(stamped), { merge: true })
    return write.then(() => {
      savedTimetablesMutationGeneration.set(key, (savedTimetablesMutationGeneration.get(key) ?? 0) + 1)
      invalidateTimetableReadEpoch(uid)
      const current = lsGet(CACHE.savedTimetables(uid, semesterKey)) ?? []
      const exists = current.some(item => item.id === committed.id)
      lsSet(CACHE.savedTimetables(uid, semesterKey), exists ? current.map(item => item.id === committed.id ? committed : item) : [...current, committed])
      return committed
    }).finally(() => {
      if (idx >= 0) return
      const remaining = (pending.get(entry.id) ?? 1) - 1
      if (remaining) pending.set(entry.id, remaining)
      else pending.delete(entry.id)
      if (pending.size === 0) pendingSavedCreates.delete(key)
    })
  }
  // 캐시 갱신
  lsSet(CACHE.savedTimetables(uid, semesterKey),
    idx >= 0 ? cached.map((e, i) => (i === idx ? stamped : e)) : [...cached, stamped]
  )
  if (fsOk(uid)) fsSet(ref.save(uid, stamped.id), stamped)
}

export function invalidateManagedTimetableReads(uid, semesterKey) {
  const key = readKey(uid, requireSemesterKey(semesterKey))
  timetableMutationGeneration.set(key, (timetableMutationGeneration.get(key) ?? 0) + 1)
  invalidateTimetableReadEpoch(uid)
  savedTimetablesMutationGeneration.set(key, (savedTimetablesMutationGeneration.get(key) ?? 0) + 1)
  invalidateTimetableReadEpoch(uid)
}

export function deleteTimetableEntry(uid, entryId, semesterKey, options = {}) {
  requireSemesterKey(semesterKey)
  if (!uid || !entryId) return
  const key = readKey(uid, semesterKey)
  const removeLocal = () => {
    savedTimetablesMutationGeneration.set(key, (savedTimetablesMutationGeneration.get(key) ?? 0) + 1)
    invalidateTimetableReadEpoch(uid)
    const cached = lsGet(CACHE.savedTimetables(uid, semesterKey)) ?? []
    lsSet(CACHE.savedTimetables(uid, semesterKey), cached.filter(entry => entry.id !== entryId))
  }
  savedTimetablesMutationGeneration.set(key, (savedTimetablesMutationGeneration.get(key) ?? 0) + 1)
  invalidateTimetableReadEpoch(uid)
  if (options.waitForRemote) {
    if (!fsOk(uid)) return Promise.reject(new Error('TIMETABLE_REMOTE_UNAVAILABLE'))
    return deleteDoc(ref.save(uid, entryId)).then(removeLocal)
  }
  removeLocal()
  if (fsOk(uid)) fsDel(ref.save(uid, entryId))
}

export function getActiveId(uid, semesterKey, storage = globalThis.localStorage) {
  requireSemesterKey(semesterKey)
  try {
    const raw = storage?.getItem(CACHE.activeId(uid, semesterKey))
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

export function setActiveId(uid, id, semesterKey) {
  requireSemesterKey(semesterKey)
  if (id == null) lsRemove(CACHE.activeId(uid, semesterKey))
  else lsSet(CACHE.activeId(uid, semesterKey), id)
}

// ──────────────────────────────────────────────────────────────────────────────
// 5. 학점 데이터
// ──────────────────────────────────────────────────────────────────────────────

export async function getGrades(uid) {
  if (!uid) return normalizeGradeData(lsGet(ANON_GRADE_KEY))
  if (!fsOk(uid)) return normalizeGradeData(lsGet(CACHE.grades(uid)))
  try {
    const snap = await getDoc(ref.grades(uid))
    if (snap.exists()) {
      const data = normalizeGradeData(snap.data())
      lsSet(CACHE.grades(uid), data)
      return data
    }
  } catch {}
  return normalizeGradeData(lsGet(CACHE.grades(uid)))
}

export function saveGrades(uid, data) {
  const payload = { ...normalizeGradeData(data), updatedAt: new Date().toISOString() }
  if (!uid) {
    lsSet(ANON_GRADE_KEY, payload)
    return
  }
  lsSet(CACHE.grades(uid), payload)
  if (fsOk(uid)) {
    fsSet(ref.grades(uid), { ...payload, updatedAt: serverTimestamp() })
  }
}
